import { Component, Input, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { formattedError } from '@helpers/functions';
import { ApiService } from '@services/api-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AlertMessage } from 'src/app/models';
import { ApiService as ApiAdmin } from 'src/app/portal-administrador/services/api.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'document-modal-viewer',
    templateUrl: './document-modal.component.html',
})
export class DocumentModalComponent {
    @Input() documento;
    title = 'Visualizar Documento';

    document_source;

    generating_blob;
    err_msg: AlertMessage;
    type;

    is_image = false;
    imageMIMETypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml'];

    @ViewChild('modalDocumento') modalDocumento;

    constructor(
        private modalService: NgbModal,
        private sanitizer: DomSanitizer,
        private api: ApiService,
        private apiDocumento: ApiAdmin,
        private spinner: NgxSpinnerService
    ) {}

    // TODO move to document-modal.service.ts
    /**
     * Abre um modal com o visualizador de pdf
     * @param documento documento retornado pela api ou um dict com a chave 'end_documento'
     * @param title Título do modal
     */
    open(documento, title = 'Visualizar Documento') {
        this.title = title;
        this.spinner.show('document-modal-viewer');
        this.type = null;
        this.is_image = false;
        this.document_source = null;
        this.err_msg = null;

        if (typeof documento == 'number') {
            this.apiDocumento.getDocumentos(documento).subscribe(
                res => {
                    this.openModal(res);
                },
                err => {
                    this.err_msg = {
                        alert_class: 'alert-danger',
                        text: err['message'] || JSON.stringify(err),
                    };
                }
            );
        } else if (typeof documento == 'string') {
            this.openModal({
                end_documento: documento,
            });
        } else {
            this.openModal(documento);
        }
    }

    openModal(documento) {
        this.documento = documento;

        if (documento) {
            this.setFileLocation(documento);
        } else {
            this.spinner.hide('document-modal-viewer');
            this.err_msg = {
                alert_class: 'alert-danger',
                text: 'Documento não encontrado',
            };
        }
        this.openScrollableContentXl(this.modalDocumento);
    }

    // TODO move to document-modal.service.ts
    openUrl(pdfWithJwt, options = null, title = 'Visualizar Documento') {
        this.open(
            {
                end_documento: pdfWithJwt.url,
            },
            title
        );
    }

    // TODO move to document-modal.service.ts
    setFileLocation(documento) {
        if (documento instanceof Blob) {
            this.type = documento.type;
            this.is_image = !!this.imageMIMETypes.find(el => this.type == el);
            this.generating_blob = false;
            const url = window.URL.createObjectURL(documento);
            this.documento.link = this.sanitizer.bypassSecurityTrustUrl(url);

            documento.arrayBuffer().then(res => {
                if (this.is_image)
                    this.document_source = this._arrayBufferToBase64(res);
                else this.document_source = this._arrayBufferToUint8Array(res);
            });
            this.spinner.hide('document-modal-viewer');
        } else if (documento.file) {
            this.type = documento.file.type;
            this.is_image = !!this.imageMIMETypes.find(el => this.type == el);
            this.generating_blob = false;
            this.setPreviewFromFile(documento.file);
        } else {
            this.generateDownloadLink();
        }
    }

    public setPreviewFromFile(file: File) {
        let reader = new FileReader();

        reader.onloadend = (e: any) => {
            this.spinner.hide('document-modal-viewer');
            if (this.is_image)
                this.document_source = this._arrayBufferToBase64(
                    e.target.result
                );
            else
                this.document_source = this._arrayBufferToUint8Array(
                    e.target.result
                );
        };

        reader.readAsArrayBuffer(file);
    }

    openScrollableContent(longContent) {
        this.modalService.open(longContent, { scrollable: true });
    }
    openScrollableContentXl(content) {
        this.modalService.open(content, { scrollable: true, size: 'xl' });
    }

    getFilename(url) {
        const parts = url.split('/');
        return parts[parts.length - 1];
    }

    generateDownloadLink() {
        if (!this.documento.end_documento) {
            this.err_msg = {
                alert_class: 'alert-danger',
                text: 'Documento inválido',
            };
            this.spinner.hide('document-modal-viewer');
            return;
        }
        this.generating_blob = true;
        this.documento.filename = this.getFilename(
            this.documento.end_documento
        );

        let clear_route = '';
        let route_split = this.documento.end_documento
            .split('/')
            .filter(el => el != '');
        if (route_split[0] != 'api') {
            clear_route = route_split.slice(1).join('/');
        } else {
            clear_route = route_split.join('/');
        }

        this.downloadFile(
            `${environment.bypassCors || ''}${
                environment.baseUrl
            }/${clear_route}`
        )
            .then(value => {
                this.documento.link =
                    this.sanitizer.bypassSecurityTrustUrl(value);
                this.generating_blob = false;
                this.spinner.hide('document-modal-viewer');
            })
            .catch(error => {
                this.spinner.hide('document-modal-viewer');
                this.generating_blob = false;
                if (error.status == 404) {
                    this.err_msg = {
                        text: 'Não foi possível localizar o documento',
                        alert_class: 'alert-danger',
                    };
                } else {
                    this.err_msg = {
                        text: error['message'] || formattedError(error),
                        alert_class: 'alert-danger',
                    };
                }
            });
    }

    public async downloadFile(href): Promise<any> {
        const blob = await this.api.downloadResource(href);
        this.type = blob.type;
        this.is_image = !!this.imageMIMETypes.find(el => this.type == el);

        if (!this.is_image && blob.type === 'application/json') {
            const raw = await blob.text();
            const err = JSON.parse(raw);
            let text = '';
            text = err.message ?? raw;
            this.err_msg = {
                text,
                alert_class: 'alert-danger',
            };
        } else if (!this.is_image && blob.type !== 'application/pdf')
            this.err_msg = {
                text: `Visualização indisponível para o tipo de documento. Clique em *Download* para visualizar.`,
                alert_class: 'alert-warning',
            };
        console.info(blob);

        blob.arrayBuffer().then(res => {
            if (this.is_image)
                this.document_source = this._arrayBufferToBase64(res);
            else this.document_source = this._arrayBufferToUint8Array(res);
        });
        const url = window.URL.createObjectURL(blob);

        return url;
    }

    _arrayBufferToUint8Array(buffer) {
        var byteArray;
        if (buffer) {
            byteArray = new Uint8Array(buffer);
        }
        return byteArray;
    }

    _arrayBufferToBase64(buffer) {
        var binary = '';
        var bytes = new Uint8Array(buffer);
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }
    sanitize(url: string) {
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }
}
